import { Accessor, createEffect, For, JSXElement, mergeProps, Setter, Show, Suspense } from "solid-js";
import { NavigationState, useApp } from "./context";
import { createOptions, CreateSelectValue, Select } from "@thisbeyond/solid-select";
import { A, useNavigate } from "@solidjs/router";
import { Icon } from "./cart2";
import { AttrId } from "./bindings/AttrId";
import { AttrDefinition } from "./bindings/AttrDefinition";
import { CategoryId } from "./bindings/CategoryId";
import { Dynamic } from "solid-js/web";
import { assert } from "./util";
import { Currency } from "./api";
import { tr_str } from "./common";
import { User } from "./login";

type AttrSearchProps = {state: NavigationState, id: AttrId, def: AttrDefinition};
function ColorList({state, id, def}: AttrSearchProps) {
    type ListEntryData = {cid: number, label: string, color: string };

    const { cache } = useApp();

    const props = () => {
        const counts = state.counts();
        const attrs = cache.attrs();
        const colors = cache.colors();

        if (attrs === undefined || counts === undefined || colors === undefined) {
            return;
        }

        const c = counts.by_id[id];
        const range = c.range;
        assert(range.type === "Color");

        function onChange(value: CreateSelectValue | null) {
            if (value !== null) {
                if (value.length > 0) {
                    state.setAttr(id, { type: "Color", cids: value.map(({cid}) => cid)});
                } else {
                    state.setAttr(id, null);
                }
            }
        }
        const format = (e: ListEntryData) => {
            if (e === undefined) return;
            return (<div class="value">
                <span>{e.label}</span>
                <span class="colorblock" style={{"background-color": e.color}}></span>
            </div>);
        };

        const options: ListEntryData[] = range.colors.map((cid) => {
            const color = colors.get(cid);
            assert(color !== undefined);
            const [r, g, b] = color.rgb;
            return { cid, label: color.name.en, color: `rgb(${r},${g},${b})` };
        });

        let initialValue = [];
        const attr = state.attrs().get(id);
        if (attr !== undefined) {
            assert(attr.type === "Color");
            initialValue = attr.cids.map((cid) => options.find(o => o.cid === cid));
        }

        function extractText(e: ListEntryData) {
            return e?.label;
        }
        
        const props = createOptions(options, {
            format,
            filterable: true,
            extractText,
        });
        return mergeProps(props, {
            initialValue,
            onChange
        });
    };

    return (<Suspense>
        <Select {...props()} multiple />
    </Suspense>);
}

function LengthSelect({state, id, def}: AttrSearchProps) {
    const value = () => {
        const val = state.attrs().get(id);
        if (val === undefined) return;

        assert(val.type === "Length")
        return val.cm;
    };

    function updateValue({target}: {target: HTMLInputElement}) {
        if (target.value === "") {
            state.setAttr(id, null);
            return;
        }
        const val = parseFloat(target.value);
        if (isFinite(val)) {
            state.setAttr(id, { type: "Length", cm: val });
        }
    }

    return <input type="number" onchange={(e) => updateValue(e)} value={value()} />;
}

function VariantList({state, id, def}: AttrSearchProps) {
    type ListEntryData = { idx: number, label: string };
    const { cache } = useApp();

    function onChange(e: ListEntryData[] | null) {
        if (e === null || e.length === 0) {
            state.setAttr(id, null);
        } else {
            state.setAttr(id, { type: "Enum", idx: Array.from(e, ({idx}) => idx) });
        }
    }

    const props = () => {
        const attrs = state.attrs();
        if (attrs === undefined) return;

        const attrDefs = cache.attrs();
        if (attrDefs === undefined) return;

        const val = attrs.get(id);

        const def = attrDefs.get(id);
        assert(def.data.type === "Enum");

        const options = def.data.values.map((value, idx) => ({ idx, label: value.label.en }));

        let initialValue = [];
        if (val !== undefined) {
            assert(val.type === "Enum");
            const vals = new Set(val.idx);
            initialValue = options.filter(({idx}) => vals.has(idx));
        }

        return mergeProps(createOptions(options, {
            filterable: true,
            extractText: (e: ListEntryData) => e?.label,
            format: (e?: ListEntryData) => e?.label,
        }), {
            initialValue,
            onChange,
        });
    };

    return <Show when={props()} keyed>
        {(props) => <Select {...props} multiple />}
    </Show>
}

const field_reg = {
    "Length": LengthSelect,
    "Color": ColorList,
    "Enum": VariantList,
};

function SearchField({state, attr_set}: {state: NavigationState, attr_set: Accessor<Set<AttrId>>}) {
    const { cache } = useApp();
    const attr_ids: () => [AttrId, AttrDefinition][] = () => {
        const attrs = cache.attrs();
        const counts = state.counts();
        if (attrs === undefined) return;
        let ids: AttrId[];
        if (counts === undefined) {
            const set = attr_set();
            if (set === undefined) return;
            ids = Array.from(set.keys());
        } else {
            ids = Object.keys(counts.by_id).map(s => parseInt(s));
        }

        const arr: [AttrId, AttrDefinition][] = ids.map((id) => {
            const def = attrs.get(id);
            if (def === undefined) return;
            return [id, def];
        });
        return arr.filter((v) => v !== undefined);
    }

    return <For each={attr_ids()}>
        {([id, def]) => {
            const props = { id, def, state };
            const val = field_reg[def.data.type];
            if (val !== undefined) {
                return <div>
                    <div class="above"><label>{def.label.en}:</label></div>
                    {val(props)}
                </div>;
            }
        }}
    </For>
}

function CategorySelect({state}: {state: NavigationState}) {
    const { cache, user } = useApp();

    type ListEntryData = { c_id: CategoryId | null, label: string };

    function onChange(value: ListEntryData | null) {
        if (value !== null) {
            state.setCategory(value.c_id);
        }
    }

    const props = () => {
        const categories = cache.categories();
        const counts = cache.category_counts();
        if (categories === undefined || counts === undefined) return;

        const lang = user.lang();
        const options: ListEntryData[] = [
            { c_id: null, label: tr_str("any", lang) },
            ...categories.entries().map(([c_id, cat]) => ({ c_id, label: cat.label.en }))
        ];
        const cat = state.category();
        const initialValue = options.find(({c_id}) => c_id === cat);
        
        return mergeProps(createOptions(options, {
            filterable: true,
            disable(value: ListEntryData) {
                return (counts.get(value.c_id) ?? 0) == 0
            },
            extractText: (e: ListEntryData) => e?.label,
            format: (e?: ListEntryData) => <div class="entry"><span>{e?.label}</span><span class="tail">{counts.get(e?.c_id) ?? 0}</span></div>,
        }), {
            initialValue,
            onChange,
        });
    };
    createEffect(() => console.dir(props()));

    return <Show when={props()} keyed>
        {(props) => <Select {...props} />}
    </Show>
}

export function CurrencySelect(props2: {class?: string}) {
    const currencies: { id: Currency, label: string }[] = [
        { id: "EUR", label: "Euro" },
        { id: "USD", label: "US Dollar" },
        { id: "MAD", label: "Moroccan Dirham" },
        { id: "CNY", label: "Chinese Yuan" },
        { id: "RUB", label: "Russian Rubel" },
    ];
    const { user } = useApp();

    const props = () => {
        const init = user.currency();
        return mergeProps(createOptions(currencies, {
            format: ({label}) => label,
        }), {
            onChange: ({id}) => user.setCurrency(id),
            initialValue: currencies.find(({id}) => id === init),
            ... props2
        });
    };

    return <Select {...props()} />
}

export const Header = () => 
    (<header>
        <div class="mid">
            <h1><a href="/" >Artisan</a></h1>
            <h2><A href="/shop">Artwork from all over Morocco</A></h2>
        </div>
        <div class="side">
            <div>
                <div><a href="/impressum">Impressum</a></div>
                <User />    
            </div>
        </div>
    </header>);

export function ShopHeader({state, attr_set}: {state: NavigationState, attr_set: Accessor<Set<AttrId>>}) {
    const { cart } = useApp();

    const nav = useNavigate();
    function openCart() {
        nav("/shop#cart")
    }

    return (<div id="fixed">
        <div class="search-fields">
            <div>
                <div class="above"><label>Category:</label></div>
                <CategorySelect state={state} />
            </div>
            <SearchField state={state} attr_set={attr_set} />
        </div>
        <div class="search-fields">
            <div>
                <div class="above"><label>Currency:</label></div>
                <CurrencySelect class="right" />
            </div>
            <div>
                <div class="zstack">
                    <Icon name="in-cart" action={openCart} disabled={cart.totalQuantity == 0} />
                    <Show when={cart.totalQuantity > 0}>
                        <div class="cart-quantity">{cart.totalQuantity}</div>
                    </Show>
                </div>
            </div>
        </div>
    </div>);
}
export function ItemHeader() {
    const { cart } = useApp();

    const nav = useNavigate();
    function openCart() {
        nav("/shop#cart")
    }

    return <div id="fixed">
        <div></div>
        <div class="search-fields">
            <div>
                <div class="above"><label>Currency:</label></div>
                <CurrencySelect class="right" />
            </div>
            <div>
                <div class="zstack">
                    <Icon name="in-cart" action={openCart} disabled={cart.totalQuantity == 0} />
                    <Show when={cart.totalQuantity > 0}>
                        <div class="cart-quantity">{cart.totalQuantity}</div>
                    </Show>
                </div>
            </div>
        </div>
    </div>
}
